import { AI_APP_LABELS, AI_APPS } from '@/features/ai-apps/recipes';
import type { AiAppId, AiAppRecipe } from '@/features/ai-apps/types';
import { cn } from '@/lib/utils';

import { AI_APP_LOGOS, AI_APP_SUBTITLES } from './app-logos';

export const AppLogo = ({
  app,
  className,
}: {
  app: AiAppId;
  className?: string;
}) => (
  <img
    src={AI_APP_LOGOS[app].src}
    alt=''
    aria-hidden
    className={cn(
      'size-8 shrink-0',
      AI_APP_LOGOS[app].monochrome && 'dark:invert',
      className,
    )}
  />
);

export const AppPicker = ({
  value,
  recipes,
  onChange,
}: {
  value: AiAppId | null;
  recipes: Record<AiAppId, AiAppRecipe>;
  onChange: (app: AiAppId) => void;
}) => (
  <div
    role='group'
    aria-label='Choose your app'
    className='grid grid-cols-2 gap-3 lg:grid-cols-4'
  >
    {AI_APPS.map((app) => {
      const recipe = recipes[app];
      const selected = value === app;
      return (
        <button
          key={app}
          type='button'
          aria-pressed={selected}
          data-testid={`ai-app-${app}`}
          onClick={() => onChange(app)}
          className={cn(
            'flex min-h-32 flex-col items-start gap-2 rounded-xl border bg-card p-4 text-left shadow-xs transition-colors',
            'hover:border-primary/60 hover:bg-accent/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            selected && 'border-primary bg-accent/60 ring-2 ring-primary',
          )}
        >
          <AppLogo app={app} />
          <span className='text-base font-semibold'>{AI_APP_LABELS[app]}</span>
          <span className='text-xs text-muted-foreground'>
            {AI_APP_SUBTITLES[app]}
          </span>
          <span
            className={cn(
              'mt-auto rounded-full px-2 py-0.5 text-xs font-medium',
              recipe.available
                ? 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400'
                : 'bg-muted text-muted-foreground',
            )}
          >
            {recipe.summary}
          </span>
        </button>
      );
    })}
  </div>
);
